import React from 'react';
import PropTypes from 'prop-types';
import EmptyState from './EmptyState';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/EmptyState',
  component: EmptyState,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `EmptyState` component notifies users when a page has no data available. It encourages users to take action, such as adding new items, by displaying a call-to-action button. The component includes a user-friendly visual illustration, a helpful message, and a customizable button.\n\n',
      // When & how to use it?
      '\nUse the `EmptyState` component on pages where users might encounter an empty state, such as when no data is available due to search filters, user permissions, or the absence of any items. The component dynamically adapts based on the `pageType` prop, making it reusable across different pages.\n\n',
      // Current Usage
      `\nThe \`EmptyState\` component is currently used on multiple pages, including:
      \n- Tags
      \n- Seasons
      \n- Contacts
      \n- Notes
      \n- Routes
      \n- Sites
      \n- Visits

      \n\nIt provides consistency in design and messaging across the application.\n\n`,
      // Related Components
      `\n- **[Button](http://localhost:6006/?path=/docs/components-button--docs)**: The call-to-action button used within the EmptyState component.
       \n- **[Icons](http://localhost:6006/?path=/docs/components-icons-icons--docs)**: Icons used for visual enhancement, including the "+" symbol for the button.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    pageType: {
      control: { type: 'text' },
      description: 'Dynamic Text: `pageType` prop dynamically adapts the component for different pages \n(e.g., Tags, Seasons, Contacts etc.).\n\n',
      table: {
        type: { summary: 'string' },
        required: true,
      },
    },
    onAdd: {
      control: { type: 'function' },
      description: 'Callback function triggered when the "New Item" button is clicked.\n\nThis allows users to add a new item dynamically based on the page context.',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    buttonLink: {
      control: { type: 'text' },
      description: 'URL for the button link displayed in the `EmptyState` component.\n\nClicking the link will navigate the user to the specified path.',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
  },
};

const EmptyStateTemplate = ({ pageType, buttonLink }) => (
  <EmptyState pageType={pageType} buttonLink={buttonLink} />
);

EmptyStateTemplate.propTypes = {
  pageType: PropTypes.string.isRequired,
  buttonLink: PropTypes.string.isRequired,
};

const Template = EmptyStateTemplate.bind({});

export const TagsEmptyState = Template.bind({});
TagsEmptyState.args = {
  pageType: 'Tags',
  buttonLink: '/tags',
};
TagsEmptyState.parameters = generateStoryDocs(
  // When is this variant used?
  'Use this variant when displaying the `EmptyState` on the Tags page.\n\n',
  // Where is it currently used on the product?
  'Currently used on the Tags page to notify users when no tags are available and prompt them to add a new tag.\n\n',
);

export const SeasonsEmptyState = Template.bind({});
SeasonsEmptyState.args = {
  pageType: 'Seasons',
  buttonLink: '/seasons',
};
SeasonsEmptyState.parameters = generateStoryDocs(
  // When is this variant used?
  'Use this variant when displaying the `EmptyState` on the Seasons page.\n\n',
  // Where is it currently used on the product?
  'Currently used on the Seasons page to notify users when no seasons are available and prompt them to add a new season.\n\n',
);

export const ContactsEmptyState = Template.bind({});
ContactsEmptyState.args = {
  pageType: 'Contacts',
  buttonLink: '/contacts',
};

ContactsEmptyState.parameters = generateStoryDocs(
  // When is this variant used?
  'Use this variant when displaying the `EmptyState` on the Contacts page.\n\n',
  // Where is it currently used on the product?
  'Currently used on the Contacts page to notify users when no contacts are available and prompt them to add a new contact.\n\n',
);

export const NotesEmptyState = Template.bind({});
NotesEmptyState.args = {
  pageType: 'Notes',
  buttonLink: '/notes',
};

NotesEmptyState.parameters = generateStoryDocs(
  // When is this variant used?
  'Use this variant when displaying the `EmptyState` on the Notes page.\n\n',
  // Where is it currently used on the product?
  'Currently used on the Notes page to notify users when no notes are available and prompt them to add a new note.\n\n',
);

export const RoutesEmptyState = Template.bind({});
RoutesEmptyState.args = {
  pageType: 'Routes',
  buttonLink: '/routes',
};

RoutesEmptyState.parameters = generateStoryDocs(
  // When is this variant used?
  'Use this variant when displaying the `EmptyState` on the Routes page.\n\n',
  // Where is it currently used on the product?
  'Currently used on the Routes page to notify users when no routes are available and prompt them to add a new route.\n\n',
);

export const SitesEmptyState = Template.bind({});
SitesEmptyState.args = {
  pageType: 'Sites',
  buttonLink: '/sites',
};

SitesEmptyState.parameters = generateStoryDocs(
  // When is this variant used?
  'Use this variant when displaying the `EmptyState` on the Sites page.\n\n',
  // Where is it currently used on the product?
  'Currently used on the Sites page to notify users when no sites are available and prompt them to add a new site.\n\n',
);

export const VisitsEmptyState = Template.bind({});
VisitsEmptyState.args = {
  pageType: 'Visits',
  buttonLink: '/visits',
};

VisitsEmptyState.parameters = generateStoryDocs(
  // When is this variant used?
  'Use this variant when displaying the `EmptyState` on the Visits page.\n\n',
  // Where is it currently used on the product?
  'Currently used on the Visits page to notify users when no visits are available and prompt them to add a new visit.\n\n',
);
